import { forwardRef } from 'react'
import * as AvatarPrimitive from '@radix-ui/react-avatar'
import { cn } from '@/lib/utils'

/**
 * Avatar — аватар пользователя (`.avatar` макета): изображение или запасные инициалы.
 * Размер/радиус — из токенов; доступное имя — через `name`.
 */
export interface AvatarProps {
  src?: string
  /** Доступное имя пользователя (alt) и источник инициалов. */
  name: string
  initials?: string
  className?: string
}

/**
 * Инициалы — первые буквы (или цифры) двух первых слов. Кавычки и прочие знаки пропускаются:
 * у аккаунта компании имя — название организации, и «ООО «Ромашка»» давало инициалы «О«».
 */
export function deriveInitials(name: string): string {
  return name
    .split(/\s+/)
    .map((part) => /[\p{L}\p{N}]/u.exec(part)?.[0] ?? '')
    .filter(Boolean)
    .slice(0, 2)
    .map((char) => char.toUpperCase())
    .join('')
}

export const Avatar = forwardRef<HTMLSpanElement, AvatarProps>(
  ({ src, name, initials, className }, ref) => (
    <AvatarPrimitive.Root
      ref={ref}
      className={cn(
        'inline-flex size-avatar select-none items-center justify-center overflow-hidden rounded-round bg-accent-bg text-13 font-semibold text-accent-txt',
        className,
      )}
    >
      {src ? (
        <AvatarPrimitive.Image src={src} alt={name} className="size-full object-cover" />
      ) : null}
      <AvatarPrimitive.Fallback
        className="flex size-full items-center justify-center"
        delayMs={src ? 200 : 0}
      >
        <span aria-label={name}>{initials ?? deriveInitials(name)}</span>
      </AvatarPrimitive.Fallback>
    </AvatarPrimitive.Root>
  ),
)
Avatar.displayName = 'Avatar'
